import React from "react";
import { observer } from "mobx-react";
import {
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Typography,
  makeStyles
} from "@material-ui/core";
import PulsingDot from "../common/pulsing-dot";
import { CheckCircle, Close, Error, OpenInNew } from "@material-ui/icons";
import { WorkflowStatus } from "@graphql";
import { useGlobalStore } from "@hooks";
import { BOOKING_SLIDER, BookingProfileLocation } from "@navigation";
import { translateWorkflowError } from "../workflow-status/translate-workflow-error";

const useStyles = makeStyles((theme) => ({
  root: {
    position: "fixed",
    right: 16,
    bottom: 16,
    width: 340,
    maxWidth: "calc(100vw - 32px)",
    maxHeight: "calc(100vh - 32px)",
    overflowY: "auto",
    overflowX: "visible",
    padding: 8,
    margin: -8,
    zIndex: 1500,
    display: "flex",
    flexDirection: "column",
    gap: 8
  },
  taskCard: {
    boxShadow: theme.shadows[4],
    borderRadius: 8,
    overflow: "hidden"
  },
  statusBar: {
    height: 4
  },
  statusBarRunning: {
    background: `linear-gradient(90deg, #1976d2 0%, #64b5f6 50%, #1976d2 100%)`,
    backgroundSize: "200% 100%",
    animation: "$shimmer 1.8s linear infinite"
  },
  statusBarScheduled: {
    background: `linear-gradient(90deg, #9e9e9e 0%, #cfcfcf 50%, #9e9e9e 100%)`,
    backgroundSize: "200% 100%",
    animation: "$shimmer 2.4s linear infinite"
  },
  statusBarCompleted: {
    backgroundColor: "#4caf50"
  },
  statusBarFailed: {
    backgroundColor: theme.palette.error.main
  },
  content: {
    paddingBottom: "12px !important"
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flex: 1,
    minWidth: 0
  },
  title: {
    fontWeight: 600,
    fontSize: 14,
    lineHeight: "20px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  statusRow: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginTop: 6
  },
  statusLabel: {
    fontSize: 12,
    fontWeight: 500
  },
  statusRunning: {
    color: "#1976d2"
  },
  statusScheduled: {
    color: "#757575"
  },
  statusCompleted: {
    color: "#4caf50"
  },
  statusFailed: {
    color: theme.palette.error.main
  },
  warningText: {
    marginTop: 6,
    fontSize: 12,
    fontStyle: "italic",
    color: "#b26a00",
    lineHeight: "16px"
  },
  bookingLink: {
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
    gap: 4,
    cursor: "pointer",
    color: "#1976d2",
    fontSize: 12,
    fontWeight: 500,
    "&:hover": {
      textDecoration: "underline"
    }
  },
  errorText: {
    marginTop: 6,
    fontSize: 12,
    color: theme.palette.error.main,
    lineHeight: "16px"
  },
  iconRunning: {
    color: "#1976d2"
  },
  iconScheduled: {
    color: "#9e9e9e"
  },
  iconCompleted: {
    color: "#4caf50"
  },
  iconFailed: {
    color: theme.palette.error.main
  },
  "@keyframes shimmer": {
    "0%": { backgroundPosition: "200% 0" },
    "100%": { backgroundPosition: "-200% 0" }
  }
}));

const STATUS_CONFIG = {
  [WorkflowStatus.Scheduled]: {
    label: "Skickas...",
    barClass: "statusBarScheduled" as const,
    labelClass: "statusScheduled" as const,
    iconClass: "iconScheduled" as const
  },
  [WorkflowStatus.Running]: {
    label: "Pågår...",
    barClass: "statusBarRunning" as const,
    labelClass: "statusRunning" as const,
    iconClass: "iconRunning" as const
  },
  [WorkflowStatus.Completed]: {
    label: "Slutförd",
    barClass: "statusBarCompleted" as const,
    labelClass: "statusCompleted" as const,
    iconClass: "iconCompleted" as const
  },
  [WorkflowStatus.Failed]: {
    label: "Misslyckades",
    barClass: "statusBarFailed" as const,
    labelClass: "statusFailed" as const,
    iconClass: "iconFailed" as const
  }
};

export const WorkflowTasksOverlay = observer(() => {
  const classes = useStyles();
  const globalStore = useGlobalStore();
  const { workflowTasks } = globalStore;

  if (!workflowTasks.hasItems) {
    return null;
  }

  const openBooking = (bookingId: string) => {
    globalStore.navigation.open(
      BOOKING_SLIDER + Math.random(),
      new BookingProfileLocation(bookingId, "finance")
    );
  };

  return (
    <div className={classes.root}>
      {workflowTasks.items.map((task) => {
        const cfg = STATUS_CONFIG[task.status] ?? STATUS_CONFIG[WorkflowStatus.Running];

        return (
          <Card key={task.workflowId} className={classes.taskCard} elevation={4}>
            {/* Colored status bar at top */}
            <div className={`${classes.statusBar} ${classes[cfg.barClass]}`} />

            <CardContent className={classes.content}>
              {/* Header: icon + title + close */}
              <div className={classes.header}>
                <div className={classes.titleRow}>
                  {task.status === WorkflowStatus.Running ||
                  task.status === WorkflowStatus.Scheduled ? (
                    <PulsingDot variant="info" size={10} />
                  ) : task.status === WorkflowStatus.Completed ? (
                    <CheckCircle fontSize="small" className={classes.iconCompleted} />
                  ) : (
                    <Error fontSize="small" className={classes.iconFailed} />
                  )}
                  <div style={{ minWidth: 0 }}>
                    <Typography className={classes.title} title={task.title}>
                      {task.title}
                    </Typography>
                    {task.metadata?.label && (
                      <Typography
                        style={{ fontSize: 11, color: "#888", lineHeight: "14px" }}
                      >
                        {task.metadata.label}
                      </Typography>
                    )}
                  </div>
                </div>

                <Tooltip title="Stäng">
                  <IconButton
                    size="small"
                    onClick={() => workflowTasks.closeTask(task.workflowId)}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Tooltip>
              </div>

              {/* Status label + booking link */}
              <div className={classes.statusRow}>
                <Typography
                  className={`${classes.statusLabel} ${classes[cfg.labelClass]}`}
                >
                  {cfg.label}
                </Typography>

                {task.bookingId && (
                  <span
                    className={classes.bookingLink}
                    onClick={() => openBooking(task.bookingId)}
                  >
                    <OpenInNew style={{ fontSize: 12 }} />
                    Öppna bokning
                  </span>
                )}
              </div>

              {/* Warning message (e.g., delayed scheduled task) */}
              {task.warning && !task.errorMessage && (
                <Typography className={classes.warningText}>{task.warning}</Typography>
              )}

              {/* Error message */}
              {task.errorMessage && (
                <Typography className={classes.errorText}>
                  {translateWorkflowError(task.errorMessage)}
                </Typography>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
});
